import React, { useEffect, useState } from 'react'
import { Button, Divider, Form, message, Modal, Row, Typography } from 'antd'
import { useForm } from 'antd/es/form/Form'
import Stepper from 'components/Stepper/Stepper'
import Storage from 'util/store/Storage'
import useGenericInfo from 'hooks/useGenericInfo'
import useRecursos from '../hooks/useConfiguracionRecursos'
import { ESTADOS_RECURSO } from '../interfaces/ConfiguracionRecursos.interfaces'
import { FIELDS_NAMES_RECURSO } from '../interfaces/FieldNamesRecurso'
import useUbicacionEstaciones from '../hooks/useUbicacionEstaciones'
import PasoUbicacionEstaciones from './PasoUbicacionEstaciones'
import PasoDatosRecurso from './PasoDatosRecurso'
import { getSubtituloModal, TITULOS_MODAL_RECURSO } from '../helpers/ModalRecursos.helper'
import './index.css'

const ModalRecurso = () => {
  const { modalOpen, modo, recursoSelected, closeModal, crearRecurso, editarRecurso, fetchDetalle } = useRecursos()

  const soloLectura = modo === 'ver'
  const isWizard = modo === 'crear'
  const isEdicion = modo === 'editar'

  const { empresasHijos, organizationByUser } = useGenericInfo({ organizations: true })
  const { negocio } = Storage.getState()

  const [form] = useForm()
  const [currentStep, setCurrentStep] = useState<0 | 1>(0)
  const [submitting, setSubmitting] = useState(false)
  // Estación dueña del recurso en editar/ver: se necesita para crear ahí los
  // archivos que el usuario agregue de más.
  const [empresaRecurso, setEmpresaRecurso] = useState<number | undefined>(undefined)

  const {
    regionalesOptions,
    loadingRegionales,
    empresasOptions,
    loadingEmpresas,
    autoseleccionadas,
    onChangeOrganizacion,
    onChangeRegionales,
    fijarUbicacion,
    reset: resetUbicacion,
  } = useUbicacionEstaciones({
    form,
    activo: modalOpen,
    modoCreacion: isWizard,
    negocio,
    empresasHijos,
    organizacionesOptions: organizationByUser,
  })

  // Organización/Regional/Estación son campos del form (SelectPrefixForm se
  // auto-vincula por `name`).
  //
  // `preserve: true` es indispensable: al pasar al paso 2 el paso 1 se
  // desmonta y, sin esa opción, useWatch deja de ver los campos desmontados y
  // devolvía la lista vacía; por eso al crear salía "Selecciona al menos una
  // estación" aunque el usuario sí había elegido estaciones.
  const empresasSeleccionadas: number[] =
    Form.useWatch(FIELDS_NAMES_RECURSO.EMPRESAS, { form, preserve: true }) ?? []

  const archivosSeleccionados: File[] =
    Form.useWatch(FIELDS_NAMES_RECURSO.ARCHIVOS, { form, preserve: true }) ?? []

  // Organización y regionales son opcionales: para avanzar solo hacen falta
  // estaciones.
  const step1Valido = empresasSeleccionadas.length > 0

  // Se crea un recurso por archivo en cada estación seleccionada, así que el
  // botón deja explícito el alcance de la acción.
  const textoBotonCrear = `Crear ${archivosSeleccionados.length > 1 ? `${archivosSeleccionados.length} recursos` : 'recurso'}${
    empresasSeleccionadas.length > 1 ? ` (${empresasSeleccionadas.length} estaciones)` : ''
  }`

  // Título / subtítulo dinámicos según modo y paso
  const tituloModal = TITULOS_MODAL_RECURSO[modo!]
  const subtituloModal = getSubtituloModal(modo!, currentStep === 0 ? 1 : 2)

  // Precarga del formulario al editar/ver, o reset limpio al crear.
  //
  // En editar/ver se pinta primero lo que ya trae la fila y luego se completa
  // con el detalle (autoservicio.fnc_get_detalle_recurso), que es el que resuelve
  // la estación y la regional para mostrarlas bloqueadas.
  useEffect(() => {
    if (!modalOpen) return

    form.resetFields()
    setCurrentStep(0)

    if (!recursoSelected) {
      resetUbicacion()
      setEmpresaRecurso(undefined)
      // Todo recurso nuevo se crea activo; no se muestra el switch en el wizard.
      form.setFieldValue(FIELDS_NAMES_RECURSO.ACTIVO, true)
      return
    }

    form.setFieldsValue({
      [FIELDS_NAMES_RECURSO.TIPO]: recursoSelected.tipo_id,
      [FIELDS_NAMES_RECURSO.NOMBRE]: recursoSelected.nombre,
      [FIELDS_NAMES_RECURSO.URL]: recursoSelected.url,
      [FIELDS_NAMES_RECURSO.ACTIVO]: recursoSelected.estado_id === ESTADOS_RECURSO.ACTIVO,
    })
    setEmpresaRecurso(recursoSelected.empresa_id)
    if (recursoSelected.empresa_id) {
      fijarUbicacion({
        label: recursoSelected.empresa_nombre ?? `Estación ${recursoSelected.empresa_id}`,
        value: recursoSelected.empresa_id,
      })
    }

    let cancelado = false
    fetchDetalle(recursoSelected.id).then((detalle) => {
      if (cancelado || !detalle) return

      form.setFieldsValue({
        [FIELDS_NAMES_RECURSO.TIPO]: detalle.tipo_id,
        [FIELDS_NAMES_RECURSO.NOMBRE]: detalle.nombre,
        [FIELDS_NAMES_RECURSO.URL]: detalle.url ?? undefined,
        [FIELDS_NAMES_RECURSO.ACTIVO]: detalle.estado_id === ESTADOS_RECURSO.ACTIVO,
      })
      setEmpresaRecurso(detalle.empresa_id)
      if (detalle.empresa_id) {
        fijarUbicacion(
          {
            label: detalle.empresa_nombre ?? `Estación ${detalle.empresa_id}`,
            value: detalle.empresa_id,
          },
          detalle.regional_id
        )
      }
    })

    return () => {
      cancelado = true
    }
  }, [recursoSelected, modalOpen])

  const handleClose = () => {
    form.resetFields()
    resetUbicacion()
    setCurrentStep(0)
    setEmpresaRecurso(undefined)
    closeModal()
  }

  const next = () => {
    if (!step1Valido) return
    setCurrentStep(1)
  }

  const prev = () => setCurrentStep(0)

  const handleSubmit = async () => {
    // Las estaciones se eligen en el paso 1, que ya está desmontado: se validan
    // acá antes de enviar y, si faltan, se devuelve al usuario a ese paso.
    if (isWizard && empresasSeleccionadas.length === 0) {
      message.warning('Selecciona al menos una estación antes de crear el recurso')
      setCurrentStep(0)
      return
    }

    try {
      // Solo los campos "normales" del form (tipo, nombre, archivos, activo).
      const values = await form.validateFields([
        FIELDS_NAMES_RECURSO.TIPO,
        FIELDS_NAMES_RECURSO.NOMBRE,
        FIELDS_NAMES_RECURSO.ARCHIVOS,
      ])
      const activo = form.getFieldValue(FIELDS_NAMES_RECURSO.ACTIVO)
      const archivos: File[] = values.archivos ?? []
      const estado_id = activo ? ESTADOS_RECURSO.ACTIVO : ESTADOS_RECURSO.INACTIVO
      setSubmitting(true)

      const resultado =
        isEdicion && recursoSelected
          ? // El recurso apunta a un solo archivo: el primero reemplaza el actual
            // y los demás se crean como recursos nuevos en la misma estación.
            await editarRecurso(
              {
                id: recursoSelected.id,
                tipo_id: values.tipo_id,
                nombre: values.nombre,
                url: form.getFieldValue(FIELDS_NAMES_RECURSO.URL) ?? '',
                estado_id,
                archivo: archivos[0],
              },
              archivos.slice(1),
              empresaRecurso
            )
          : await crearRecurso(
              {
                tipo_id: values.tipo_id,
                nombre: values.nombre,
                estado_id,
                archivos,
              },
              empresasSeleccionadas
            )

      if (resultado.success) handleClose()
    } catch (error: any) {
      // Los errores de validación del form no llevan mensaje propio.
      if (error?.errorFields) return
      message.error(error?.response?.data?.message ?? error?.message ?? 'No se pudo guardar el recurso')
    } finally {
      setSubmitting(false)
    }
  }

  const pasoUbicacion = (
    <PasoUbicacionEstaciones
      form={form}
      organizacionesOptions={organizationByUser}
      regionalesOptions={regionalesOptions}
      loadingRegionales={loadingRegionales}
      empresasOptions={empresasOptions}
      loadingEmpresas={loadingEmpresas}
      autoseleccionadas={autoseleccionadas}
      onChangeOrganizacion={onChangeOrganizacion}
      onChangeRegionales={onChangeRegionales}
      soloLectura={!isWizard}
    />
  )

  const pasoDatos = (
    <PasoDatosRecurso
      form={form}
      readOnly={soloLectura}
      mostrarEstado={!isWizard}
      // En edición se pueden adjuntar varios: el primero reemplaza y el resto se
      // agregan como recursos nuevos en la misma estación.
      permitirVariosArchivos={!soloLectura}
      esEdicion={isEdicion}
    />
  )

  return (
    <Modal open={modalOpen} onCancel={handleClose} footer={null} centered width={600} destroyOnClose>
      <Row justify="center" align="middle" style={{ marginBottom: 8 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          {tituloModal}
        </Typography.Title>
      </Row>
      <Row justify="center" align="middle" style={{ marginBottom: 16 }}>
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          {subtituloModal}
        </Typography.Text>
      </Row>

      {isWizard && (
        <Stepper
          type="outline"
          controlled
          current={currentStep}
          onChange={(e: any) => step1Valido && setCurrentStep(e)}
          steps={[
            { label: 'Estaciones', key: 'ubicacion' },
            { label: 'Datos del recurso', key: 'datos' },
          ]}
        />
      )}

      <Form form={form} layout="vertical" disabled={soloLectura} style={{ marginTop: 16 }}>
        {isWizard ? (
          currentStep === 0 ? pasoUbicacion : pasoDatos
        ) : (
          // En editar/ver no hay wizard: la ubicación va arriba (bloqueada) y
          // debajo los datos que sí se pueden modificar.
          <>
            {pasoUbicacion}
            <Divider style={{ margin: '16px 0' }} />
            {pasoDatos}
          </>
        )}
      </Form>

      {!soloLectura && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          {isWizard && currentStep === 1 && (
            <Button block onClick={prev}>
              Atrás
            </Button>
          )}

          {isWizard && currentStep === 0 ? (
            <Button
              type="primary"
              block
              disabled={!step1Valido}
              onClick={next}
              style={{ borderRadius: 12, fontWeight: 'bold' }}
            >
              Siguiente
            </Button>
          ) : (
            <Button
              type="primary"
              block
              loading={submitting}
              onClick={handleSubmit}
              style={{ borderRadius: 12, fontWeight: 'bold' }}
            >
              ✓ {isEdicion ? 'Guardar cambios' : textoBotonCrear}
            </Button>
          )}
        </div>
      )}
    </Modal>
  )
}

export default ModalRecurso
